import React, { useState, useRef, useMemo, useEffect } from 'react';
import FormField from '../FormField/FormField';
import SubmitButton from '../SubmitButton/SubmitButton';
import './IntegrationModal.css';
import { getSyncOrganisation, syncOrganisationById } from '../../api/orgsApi';

const ANIMATION_DURATION_MS = 300; // должен совпадать с CSS transition

const IntegrationModal = ({ isVisible, onClose, selectedItemId }) => {
  const [name, setName] = useState('');
  const [orgList, setOrgList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorText, setErrorText] = useState(''); // ✅ локальное состояние ошибки

  const [shouldRender, setShouldRender] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const buttonRef = useRef(null);

  const fieldConfigurations = useMemo(
    () => [{ key: 'name', label: 'Название организации', type: 'text' }],
    []
  );

  // Механизм плавного появления и удаления из DOM
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsActive(true));
      });
      return () => cancelAnimationFrame(raf);
    } else {
      setIsActive(false);
      const t = setTimeout(() => {
        setShouldRender(false);
      }, ANIMATION_DURATION_MS);
      return () => clearTimeout(t);
    }
  }, [isVisible]);

  // ===== Закрытие по ESC =====
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClosePopup();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]); // слушаем только, когда модалка открыта

  const handleChange = (e) => {
    setName(e.target.value);
    setErrorText(''); // очищаем ошибку при вводе
  };

  const handleSubmit = async () => {
    // Проверка на пустое поле
    if (!name.trim()) {
      setErrorText('Введите название организации');
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    setErrorText(''); // очищаем прошлую ошибку

    try {
      const response = await getSyncOrganisation({ name, organisation_id: selectedItemId });
      let data = response?.data;

      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          console.warn('API вернул строку, не являющуюся JSON:', data);
          setErrorText('Некорректный ответ сервера');
          data = [];
        }
      }

      if (!Array.isArray(data)) {
        console.warn('Ожидался массив организаций, пришло другое:', data);
        setErrorText('Сервер вернул неверный формат данных');
        data = [];
      }

      setOrgList(data);
    } catch (err) {
      console.error('Ошибка при поиске организаций:', err);
      setErrorText(
        'Ошибка при запросе. Проверьте подключение или повторите позже.'
      );
      setOrgList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async (orgId) => {
    try {
      await syncOrganisationById({
        uuid: orgId,
        organisation_id: selectedItemId,
      });
      onClose();
    } catch (err) {
      console.error('Ошибка при синхронизации:', err);
      alert('Ошибка при синхронизации');
    }
  };

  const handleClosePopup = () => {
    setName('');
    setOrgList([]);
    setHasSearched(false);
    setErrorText('');
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  if (!shouldRender) return null;

  const modalCls = `integrationModal ${isActive ? 'show' : 'hide'}`;
  const overlayCls = `overlay ${isActive ? 'show' : 'hide'}`;

  return (
    <>
      <div onClick={handleClosePopup} className={overlayCls}></div>

      <div className={modalCls}>
        {fieldConfigurations.map((f) => (
          <FormField
            key={f.key}
            label={f.label}
            type={f.type}
            name={f.key}
            value={name}
            onChange={handleChange}
            error={errorText} // ✅ ошибка отображается прямо под полем
            placeholder={f.placeholder}
            required={true}
            modifierClassesFormField={{
              field: 'integrationModal__wrapper-field',
            }}
            onKeyDown={onKeyDown}
          />
        ))}

        <SubmitButton
          ref={buttonRef}
          text='Найти'
          disabled={!name}
          onClick={handleSubmit}
          type='button'
          className='integrationModal__button'
          isLoading={isLoading}
        />

        {Array.isArray(orgList) && orgList.length > 0 && (
          <div className='integrationModal__results'>
            <h4 className='integrationModal__title-list'>Результаты поиска</h4>
            <ul className='integrationModal__listOrg'>
              {orgList.map((org) => (
                <li
                  key={org.id ?? org.name}
                  className='integrationModal__org-item'
                >
                  <span>{org.name ?? 'Без названия'}</span>
                  <button
                    className='integrationModal__sync-button'
                    onClick={() => handleSync(org.id)}
                  >
                    Синхронизировать
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasSearched &&
          !isLoading &&
          Array.isArray(orgList) &&
          orgList.length === 0 &&
          !errorText && ( // показываем "ничего не найдено" только если нет ошибок
            <div className='integrationModal__no-results'>
              Ничего не найдено
            </div>
          )}
      </div>
    </>
  );
};

export default IntegrationModal;
